import React from 'react';
import useSEO from "./useSEO";

const ManufacturingStockAudit = () => {
  useSEO({
    title: "Manufacturing Stock Audit | Manufacturing Audit Services in Hyderabad | Inventory Verification Experts",
    description:
      "Ensure accuracy and control with our professional manufacturing stock audit services in Hyderabad. We verify raw materials, WIP, and finished goods, helping manufacturers maintain efficiency, prevent losses, and improve financial accuracy.",
    keywords:
      "manufacturing stock audit, manufacturing audit services, stock verification Hyderabad, raw material audit, work in progress audit, WIP stock audit, finished goods audit, factory inventory audit, production audit, inventory verification services, manufacturing compliance audit, warehouse stock audit, industrial stock audit",
    ogTitle: "Manufacturing Stock Audit - Hyderabad Experts",
    ogDescription:
      "Accurate manufacturing stock audits covering raw materials, WIP, and finished goods. Improve efficiency and financial control with expert audits.",
    ogImage: "img/manufacturing-stock-audit.png",
    twitterTitle: "Manufacturing Stock Audit - Hyderabad Experts",
    twitterDescription:
      "Comprehensive stock verification and process audit for manufacturers. Optimize cost, prevent losses, and ensure compliance.",
    twitterImage: "img/manufacturing-stock-audit.png"
  });

  return (
    <div>
      <>
        {/* Section 1 - Hero */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Left - Text */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4 text-primary">
                  Manufacturing Stock Audit
                </h1>
                <p className="mb-4">
                  A <b>Manufacturing Stock Audit</b> ensures that every item in
                  your factory — from raw materials and work-in-progress (WIP)
                  to finished goods — is accurately recorded, valued, and
                  controlled. Our team of experts conducts detailed verification
                  to identify variances, minimize waste, and enhance production
                  efficiency.
                </p>
                <p>
                  <b>What is a Manufacturing Stock Audit?</b>
                </p>
                <p>
                  It’s a systematic process that compares actual physical stock
                  with book or ERP records to ensure data accuracy and
                  transparency in your manufacturing operations. This audit
                  reduces financial discrepancies and improves material planning.
                </p>
              </div>

              {/* Right - Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/manufacturing-stock-audit.jpg"
                  alt="Manufacturing Stock Audit"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Audit Process */}
        <section className="overflow-hidden py-5 bg-light border-top">
          <div className="container">
            <div className="row align-items-center">
              {/* Left - Image */}
              <div className="col-lg-6 text-center mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/manufacturing-audit-process.jpg"
                    alt="Manufacturing Audit Process"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Right - Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Manufacturing Audit Process</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Raw Material Verification</b>
                  </li>
                  <p>
                    We check material receipts, consumption, and balances to
                    ensure accurate reporting of inputs and raw stock.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Work-in-Progress (WIP) Validation</b>
                  </li>
                  <p>
                    Detailed review of production batches, stage-wise movement,
                    and process losses to ensure accuracy.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Finished Goods Reconciliation</b>
                  </li>
                  <p>
                    Physical verification of finished products ready for
                    dispatch and reconciliation with ERP data.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Scrap & Rejection Analysis</b>
                  </li>
                  <p>
                    Ensuring that scrap generation and rejected materials are
                    properly documented and accounted for.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Inventory Valuation & Reporting</b>
                  </li>
                  <p>
                    We validate your valuation methods (FIFO, LIFO, Weighted
                    Average) to ensure financial accuracy and compliance.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Benefits */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">Benefits of Manufacturing Stock Audit</h2>
              <p className="lead">
                Enhance operational efficiency, ensure accurate reporting, and
                gain full visibility over your manufacturing assets.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-graph-up text-success fs-3 mb-2"></i>
                <h5>Inventory Accuracy</h5>
                <p>Eliminate stock mismatches and maintain reliable inventory records.</p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Cost Optimization</h5>
                <p>Identify slow-moving, obsolete, or excess materials to reduce carrying costs.</p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Improved Compliance</h5>
                <p>Ensure accurate valuation and accounting for statutory and financial audits.</p>
              </div>
            </div>

            <div className="row text-center mt-4">
              <div className="col-md-4 mb-4">
                <i className="bi bi-box-seam text-success fs-3 mb-2"></i>
                <h5>Enhanced Production Control</h5>
                <p>Monitor WIP and optimize materials for uninterrupted production flow.</p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-clipboard-data text-success fs-3 mb-2"></i>
                <h5>Transparent Reporting</h5>
                <p>Comprehensive audit reports with variance analysis and recommendations.</p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Data-Driven Insights</h5>
                <p>Use audit results to improve procurement, planning, and warehouse systems.</p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default ManufacturingStockAudit;
